// client/src/v2/pages/ExplorePage.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TopBar from "../components/layout/TopBar";
import LeftPanel from "../components/sidebar/LeftPanel";
import MobileSchoolCardsDeck from "../components/mobile/MobileSchoolCardsDeck";
import MapCanvas from "../components/map/MapCanvas";
import SchoolMarkersV2Layer from "../components/map/SchoolMarkersV2Layer";
import LegendControl from "../components/map/LegendControl";
import MapZoomIndicator from "../components/map/MapZoomIndicator";
import PhoneMapViewportClamp from "../components/map/PhoneMapViewportClamp";
import SelectedCatchmentsLayers from "../components/map/SelectedCatchmentsLayers";
import FitCatchmentBounds from "../components/map/FitCatchmentBounds";
import SchoolDetailDrawer from "../components/drawer/SchoolDetailDrawer";
import FilterDrawer from "../components/filters/FilterDrawer";
import { useFilterState } from "../components/filters/useFilterState";
import { useFilteredSchools } from "../components/filters/useFilteredSchools";
import PanToHomeV2 from "../components/map/PanToHomeV2";
import PanToSchoolV2 from "../components/map/PanToSchoolV2";
import HomeMarkerV2Layer from "../components/map/HomeMarkerV2Layer";
import SetHomeOnMapClickV2 from "../components/map/SetHomeOnMapClickV2";
import TransportRouteLayer from "../components/transport/TransportRouteLayer";
import FitRouteBounds from "../components/transport/FitRouteBounds";
import FitHomeSchoolBounds from "../components/transport/FitHomeSchoolBounds";
import { DEFAULT_POSTCODE } from "../config/defaultPostcode";
import { useHomeLocation } from "../domains/home/useHomeLocation";
import { useSchools } from "../domains/schools/useSchools";
import { useSelectedCatchments } from "../domains/catchmentV2/useSelectedCatchments";
import useTransportUI from "../domains/transport/useTransportUI";
import { useCatchmentCheck } from "../domains/catchmentCheck/useCatchmentCheck";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  sortSchoolsByHomeDistance,
  useSchoolListBuckets,
} from "../hooks/useSchoolListBuckets";
import { useFavourites } from "../favourites/useFavourites";
import { useApiHealth } from "../hooks/useApiHealth";
import { UI_MSG } from "../config/uiMessages";
import RankingPage from "../ranking/RankingPage";
import ComparePage from "../compare/ComparePage";
import HomePage from "../home/HomePage";
import AccountPage from "../account/AccountPage";
import SchoolJourneyPage from "../journey/SchoolJourneyPage";
import { hasSchoolJourney } from "../journey/journeyRegistry";
import {
  currentExploreHistoryOwnsGuide,
  readExploreHistoryFromState,
  readExploreHistoryFromUrl,
  sameExploreSnapshot,
  snapshotFromView,
  writeExploreHistory,
} from "../navigation/exploreHistory";
import "../ranking/ranking.css";

// Desktop shell; phone uses dvh on the App shell. Page fills remaining height above footer.
const SHELL_STYLE_DESKTOP = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};
const SHELL_STYLE_PHONE = {
  height: "100%",
  minHeight: 0,
  maxHeight: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const BODY_STYLE = { flex: 1, display: "flex", minHeight: 0 };
const MAP_WRAP_STYLE = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  position: "relative",
};
/* overflow hidden: search stays put; lists scroll inside LeftPanel */
const SIDEBAR_STYLE = {
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  alignSelf: "stretch",
};

const PHONE_MAX_WIDTH = "(max-width: 767px)";
const EXPLORE_PAGE_KEY = "v2.explorePage";
const COMPARE_SEED_KEY = "v2.compareSeedIds";
const EXPLORE_PAGES = new Set(["map", "home", "ranking", "compare", "account"]);

function readExplorePage() {
  try {
    const raw = sessionStorage.getItem(EXPLORE_PAGE_KEY);
    if (EXPLORE_PAGES.has(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "map";
}

function readInitialExploreSnapshot() {
  const fromUrl = readExploreHistoryFromUrl();
  if (fromUrl) return fromUrl;
  return snapshotFromView({ page: readExplorePage(), journeySchoolId: null });
}

function readCompareSeedIds() {
  try {
    const raw = sessionStorage.getItem(COMPARE_SEED_KEY);
    const ids = JSON.parse(raw);
    if (!Array.isArray(ids)) return [];
    return ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .slice(0, 4);
  } catch {
    return [];
  }
}

export default function ExplorePage({ onNavigateTrust }) {
  const initialExplore = useMemo(() => readInitialExploreSnapshot(), []);
  const [legendOpen, setLegendOpen] = useState(false);
  const [drawerSchool, setDrawerSchool] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [explorePage, setExplorePage] = useState(initialExplore.page);
  const [compareSeedIds, setCompareSeedIds] = useState(readCompareSeedIds);
  const [journeySchool, setJourneySchool] = useState(() =>
    initialExplore.journeySchoolId ? { id: initialExplore.journeySchoolId } : null
  );
  const historySnapshotRef = useRef(null);
  const applyingHistoryRef = useRef(false);
  const historyReadyRef = useRef(false);
  const rankingOpen = explorePage === "ranking";
  const compareOpen = explorePage === "compare";
  const homeOpen = explorePage === "home";
  const accountOpen = explorePage === "account";
  const journeyOpen = Boolean(journeySchool);
  const isPhoneLayout = useMediaQuery(PHONE_MAX_WIDTH);
  const { filters, updateFilter, resetFilters, hasActiveFilters } = useFilterState();
  /** Last school focused from list row or map pin (drawer/transport take precedence on map ring). */
  const [mapAnchorSchool, setMapAnchorSchool] = useState(null);

  const {
    postcode,
    setPostcode,
    homePosition,
    homeError,
    submitPostcode,
    setHomeFromMap,
  } = useHomeLocation(DEFAULT_POSTCODE);

  const { schools, schoolsNear, openCatchment, schoolsError } = useSchools();
  const { checked: apiHealthChecked, reachable: apiReachable } = useApiHealth();
  const showServerBanner = apiHealthChecked && !apiReachable;
  /** Avoid stacking the same outage as both global + list alerts */
  const listSchoolsError = showServerBanner ? null : schoolsError;
  const { filteredSchools, filterMeta, filterMetaError } = useFilteredSchools(
    schools,
    filters,
    homePosition
  );

  const { selectedIds, catchmentsBySchoolId, toggleSchool, clearAllCatchments, geometryError } =
    useSelectedCatchments();

  const transport = useTransportUI({ homePosition, postcode });

  // Close drawer when transport opens for a different school
  const transportSchoolId = transport.transportSchool?.id;
  useEffect(() => {
    if (transportSchoolId && drawerSchool?.id && transportSchoolId !== drawerSchool.id) {
      setDrawerSchool(null);
    }
  }, [transportSchoolId, drawerSchool?.id]);

  const schoolIds = useMemo(() => {
    if (!Array.isArray(schools)) return [];
    return schools.map((s) => s.id).filter((id) => typeof id === "number");
  }, [schools]);

  const { loading: catchmentLoading, error: catchmentCheckError, catchmentCheckBySchoolId } =
    useCatchmentCheck({
      schoolIds,
      homeLocation: transport.homeLocation,
    });

  const inCatchmentSchools = useMemo(() => {
    if (!Array.isArray(filteredSchools)) return [];
    const map = catchmentCheckBySchoolId || {};
    return filteredSchools.filter((s) => !!map?.[s.id]?.inCatchment);
  }, [filteredSchools, catchmentCheckBySchoolId]);

  const homeLat = homePosition?.[0];
  const homeLon = homePosition?.[1];
  useEffect(() => {
    setMapAnchorSchool(null);
  }, [homeLat, homeLon]);

  const { topItems, middleItems, nearbyItems, bottomItems } = useSchoolListBuckets({
    inCatchmentSchools,
    catchmentCheckBySchoolId,
    allSchools: filteredSchools,
    schoolsNear,
    homeLocation: transport.homeLocation,
    ensureSchoolIds: selectedIds,
  });

  const {
    favouriteCount,
    filterOn: favouritesFilterOn,
    isFavourite,
    toggleFavourite,
    toggleFavouritesFilter,
    filterSchools,
  } = useFavourites();

  const filterMobileBucket = useCallback(
    (items) => {
      let list = filterSchools(items);
      if (Array.isArray(selectedIds) && selectedIds.length && Array.isArray(items)) {
        const kept = new Set(list.map((s) => s.id));
        const missing = items.some(
          (s) => selectedIds.includes(s.id) && !kept.has(s.id)
        );
        if (missing) {
          const keep = new Set([...kept, ...selectedIds]);
          list = items.filter((s) => keep.has(s.id));
        }
      }
      return sortSchoolsByHomeDistance(list, transport.homeLocation);
    },
    [filterSchools, selectedIds, transport.homeLocation]
  );

  const mobileTopItems = useMemo(
    () => filterMobileBucket(topItems),
    [filterMobileBucket, topItems]
  );
  const mobileMiddleItems = useMemo(
    () => filterMobileBucket(middleItems),
    [filterMobileBucket, middleItems]
  );
  const mobileNearbyItems = useMemo(
    () => filterMobileBucket(nearbyItems),
    [filterMobileBucket, nearbyItems]
  );
  const mobileBottomItems = useMemo(
    () => filterMobileBucket(bottomItems),
    [filterMobileBucket, bottomItems]
  );

  const handleOpenSchoolDetails = useCallback(
    (s) => {
      setMapAnchorSchool(s);
      setDrawerSchool((prev) => (prev?.id === s.id ? null : s));
      setFilterDrawerOpen(false);
      if (transport.transportSchool?.id && transport.transportSchool.id !== s.id) {
        transport.closeTransport();
      }
    },
    [transport]
  );

  const handleSchoolRowClick = useCallback(
    (s) => {
      setMapAnchorSchool(s);
      setDrawerSchool((prev) => (prev && prev.id !== s.id ? null : prev));
      if (transport.transportSchool?.id && transport.transportSchool.id !== s.id) {
        transport.closeTransport();
      }
    },
    [transport]
  );

  /**
   * Map pin: show the row, draw the catchment, and open the drawer.
   * A second click, when the catchment is already on, closes the catchment and the drawer.
   */
  const handleSchoolMarkerClick = useCallback(
    (schoolId) => {
      const s = schools.find((x) => x.id === schoolId);
      if (!s) return;
      const id = s.id;
      const catchmentOn = selectedIds.includes(id);

      if (catchmentOn) {
        void toggleSchool(id);
        setMapAnchorSchool((prev) => (prev?.id === id ? null : prev));
        setDrawerSchool((prev) => (prev?.id === id ? null : prev));
        if (transport.transportSchool?.id === id) transport.closeTransport();
        return;
      }

      setMapAnchorSchool(s);
      setDrawerSchool(s);
      setFilterDrawerOpen(false);
      if (transport.transportSchool?.id && transport.transportSchool.id !== s.id) {
        transport.closeTransport();
      }
      void toggleSchool(id);
    },
    [schools, selectedIds, toggleSchool, transport]
  );

  const handleCloseDrawer = useCallback(() => setDrawerSchool(null), []);

  const handleOpenFilters = useCallback(() => {
    if (filterDrawerOpen) {
      setFilterDrawerOpen(false);
      resetFilters();
      return;
    }
    setDrawerSchool(null);
    if (transport.transportSchool) transport.closeTransport();
    setFilterDrawerOpen(true);
  }, [filterDrawerOpen, resetFilters, transport]);
  const handleToggleFiltersMobile = useCallback(() => {
    if (filterDrawerOpen) {
      setFilterDrawerOpen(false);
      return;
    }
    setDrawerSchool(null);
    if (transport.transportSchool) transport.closeTransport();
    setFilterDrawerOpen(true);
  }, [filterDrawerOpen, transport]);
  const handleCloseFilters = useCallback(() => setFilterDrawerOpen(false), []);

  useEffect(() => {
    if (!isPhoneLayout || !drawerSchool) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDrawerSchool(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPhoneLayout, drawerSchool]);

  const handleToggleLegend = useCallback(() => setLegendOpen((prev) => !prev), []);
  const handleCloseLegend = useCallback(() => setLegendOpen(false), []);

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPLORE_PAGE_KEY, explorePage);
    } catch {
      /* ignore */
    }
  }, [explorePage]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COMPARE_SEED_KEY, JSON.stringify(compareSeedIds));
    } catch {
      /* ignore */
    }
  }, [compareSeedIds]);

  // Fill guide school stub { id } once directory loads.
  useEffect(() => {
    const id = Number(journeySchool?.id);
    if (!Number.isFinite(id) || !Array.isArray(schools) || !schools.length) return;
    if (journeySchool?.name || journeySchool?.display_name) return;
    const found = schools.find((row) => Number(row.id) === id);
    if (found) setJourneySchool(found);
  }, [schools, journeySchool]);

  // Keep browser history in sync with Explore views (map / ranking / compare / home / account / guide).
  useEffect(() => {
    const snap = snapshotFromView({
      page: explorePage,
      journeySchoolId: journeySchool?.id || null,
    });

    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
      historySnapshotRef.current = snap;
      return undefined;
    }

    if (!historyReadyRef.current) {
      historySnapshotRef.current = writeExploreHistory(snap, "replace");
      historyReadyRef.current = true;
      return undefined;
    }

    if (sameExploreSnapshot(historySnapshotRef.current, snap)) return undefined;
    historySnapshotRef.current = writeExploreHistory(snap, "push");
    return undefined;
  }, [explorePage, journeySchool?.id]);

  useEffect(() => {
    function applySnapshot(raw) {
      const snap =
        readExploreHistoryFromState(raw) ||
        readExploreHistoryFromUrl() ||
        snapshotFromView({ page: "map", journeySchoolId: null });
      applyingHistoryRef.current = true;
      historySnapshotRef.current = snap;
      setExplorePage(snap.page || "map");
      if (snap.journeySchoolId) {
        const found =
          (schools || []).find((row) => Number(row.id) === Number(snap.journeySchoolId)) ||
          { id: snap.journeySchoolId };
        setJourneySchool(found);
      } else {
        setJourneySchool(null);
      }
    }

    function onPopState(event) {
      applySnapshot(event.state);
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [schools]);

  function closeSchoolJourney() {
    if (currentExploreHistoryOwnsGuide()) {
      window.history.back();
      return;
    }
    setJourneySchool(null);
    setExplorePage("map");
  }

  function approxMilesFromHome(school) {
    const lat1 = Number(homePosition?.[0]);
    const lon1 = Number(homePosition?.[1]);
    const lat2 = Number(school?.lat);
    const lon2 = Number(school?.lon);
    if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
    const dy = (lat2 - lat1) * 69;
    const dx = (lon2 - lon1) * 69 * Math.cos((lat1 * Math.PI) / 180);
    return Math.sqrt(dx * dx + dy * dy);
  }

  function closestSchoolIds(limit = 3) {
    return [...(schools || [])]
      .map((school) => ({ id: school.id, miles: approxMilesFromHome(school) }))
      .filter((row) => Number.isFinite(row.miles) && Number.isFinite(Number(row.id)))
      .sort((a, b) => a.miles - b.miles)
      .slice(0, limit)
      .map((row) => Number(row.id));
  }

  function openCompare(ids) {
    const seed = (Array.isArray(ids) ? ids : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .slice(0, 4);
    setJourneySchool(null);
    setCompareSeedIds(seed);
    setExplorePage("compare");
  }

  function openPage(which) {
    setJourneySchool(null);
    if (which === "compare") {
      openCompare(closestSchoolIds(3));
      return;
    }
    if (EXPLORE_PAGES.has(which)) {
      setExplorePage(which);
      return;
    }
    setExplorePage("map");
  }

  function openSchoolJourney(school) {
    if (!hasSchoolJourney(school)) return;
    setExplorePage("map");
    setJourneySchool(school);
  }

  function openSchoolCatchmentFromGuide(school) {
    const found =
      (schools || []).find((row) => row.id === school?.id) || school;
    if (!found?.id) return;
    // Leave the guide entry so browser Back can return to it.
    setJourneySchool(null);
    setExplorePage("map");
    setMapAnchorSchool(found);
    setDrawerSchool(found);
    setFilterDrawerOpen(false);
    if (transport.transportSchool?.id && transport.transportSchool.id !== found.id) {
      transport.closeTransport();
    }
    if (!selectedIds.includes(found.id)) {
      void toggleSchool(found.id);
    }
  }

  function openSchoolFromPage(row) {
    const found =
      (schools || []).find((school) => school.id === row.id) || row;
    if (!found?.id) return;
    openPage("map");
    setDrawerSchool(found);
    setMapAnchorSchool(found);
    setFilterDrawerOpen(false);
    if (transport.transportSchool?.id && transport.transportSchool.id !== found.id) {
      transport.closeTransport();
    }
    if (!selectedIds.includes(found.id)) {
      void toggleSchool(found.id);
    }
  }

  const mapFocusSchool =
    drawerSchool ?? transport.transportSchool ?? mapAnchorSchool ?? null;

  return (
    <div
      className={`v2-page${isPhoneLayout ? " v2-page--phone" : ""}`}
      style={isPhoneLayout ? SHELL_STYLE_PHONE : SHELL_STYLE_DESKTOP}
    >
      <TopBar
        postcode={postcode}
        defaultPostcode={DEFAULT_POSTCODE}
        onPostcodeChange={setPostcode}
        onChangePostcode={setPostcode}
        onPostcodeSubmit={submitPostcode}
        onSubmitPostcode={submitPostcode}
        error={homeError}
        onToggleFilters={
          isPhoneLayout && !rankingOpen && !compareOpen && !homeOpen && !accountOpen && !journeyOpen
            ? handleToggleFiltersMobile
            : undefined
        }
        hasActiveFilters={hasActiveFilters}
        onOpenRanking={() => openPage("ranking")}
        onOpenCompare={() => openPage("compare")}
        onOpenHome={() => openPage("home")}
        onOpenAccount={() => openPage("account")}
        onOpenMap={() => openPage("map")}
        rankingOpen={rankingOpen}
        compareOpen={compareOpen}
        homeOpen={homeOpen}
        accountOpen={accountOpen}
      />

      {showServerBanner ? (
        <div className="v2-service-banner" role="alert">
          {UI_MSG.SERVER_UNREACHABLE}
        </div>
      ) : null}

      {journeyOpen ? (
        <SchoolJourneyPage
          school={journeySchool}
          onBack={closeSchoolJourney}
          onOpenCatchmentMap={openSchoolCatchmentFromGuide}
        />
      ) : homeOpen ? (
        <HomePage
          postcode={postcode}
          error={homeError}
          onSubmitPostcode={submitPostcode}
          onUseLocation={setHomeFromMap}
          onOpenPage={openPage}
        />
      ) : accountOpen ? (
        <AccountPage
          postcode={postcode}
          savedSchools={(schools || []).filter((school) => isFavourite(school.id))}
          onOpenPage={openPage}
          onOpenSchool={(school) => {
            openPage("map");
            setDrawerSchool(school);
            setMapAnchorSchool(school);
          }}
        />
      ) : rankingOpen ? (
        <RankingPage
          home={{
            lat: homePosition?.[0],
            lon: homePosition?.[1],
            postcode,
          }}
          onOpenSchool={openSchoolFromPage}
          directory={schools}
          catchmentCheckBySchoolId={catchmentCheckBySchoolId}
          catchmentReady={!catchmentLoading}
        />
      ) : compareOpen ? (
        <ComparePage
          key={compareSeedIds.join("-") || "compare-empty"}
          home={{
            lat: homePosition?.[0],
            lon: homePosition?.[1],
            postcode,
          }}
          directory={schools}
          catchmentCheckBySchoolId={catchmentCheckBySchoolId}
          catchmentReady={!catchmentLoading}
          initialIds={compareSeedIds}
        />
      ) : (
      <div className={`v2-body${isPhoneLayout ? " v2-body--phone" : ""}`} style={BODY_STYLE}>
        {!isPhoneLayout ? (
          <div className="v2-sidebar" style={SIDEBAR_STYLE}>
            <LeftPanel
              inCatchmentSchools={inCatchmentSchools}
              catchmentCheckBySchoolId={catchmentCheckBySchoolId}
              catchmentLoading={catchmentLoading}
              catchmentCheckError={
                showServerBanner ? null : catchmentCheckError
              }
              schoolsError={listSchoolsError}
              geometryError={showServerBanner ? null : geometryError}
              allSchools={filteredSchools}
              schoolsNear={schoolsNear}
              openCatchment={openCatchment}
              selectedIds={selectedIds}
              onToggleSchool={toggleSchool}
              onClearAllCatchments={clearAllCatchments}
              homeLocation={transport.homeLocation}
              transportSchool={transport.transportSchool}
              onOpenTransport={transport.toggleTransportForSchool}
              onCloseTransport={transport.closeTransport}
              onSelectRoute={transport.onSelectRoute}
              onHoverRoute={transport.onHoverRoute}
              onLeaveRoute={transport.onLeaveRoute}
              onLeaveOptionsList={transport.onLeaveOptionsList}
              onClearRoute={transport.onClearRoute}
              onOpenSchoolDetails={handleOpenSchoolDetails}
              onSchoolRowClick={handleSchoolRowClick}
              drawerSchoolId={drawerSchool?.id}
              focusedSchoolId={mapFocusSchool?.id ?? null}
              onOpenFilters={handleOpenFilters}
              hasActiveFilters={hasActiveFilters}
              favouriteCount={favouriteCount}
              favouritesFilterOn={favouritesFilterOn}
              onToggleFavouritesFilter={toggleFavouritesFilter}
              isFavourite={isFavourite}
              onToggleFavourite={toggleFavourite}
              onOpenCompare={openCompare}
            />
          </div>
        ) : null}

        {isPhoneLayout && (drawerSchool || filterDrawerOpen) ? (
          <button
            type="button"
            className="v2-mobile-drawer-backdrop"
            aria-label={filterDrawerOpen ? "Close filters" : "Close school details"}
            onClick={filterDrawerOpen ? handleCloseFilters : handleCloseDrawer}
          />
        ) : null}

        {drawerSchool ? (
          <div
            className={`v2-drawer-sidebar${isPhoneLayout ? " v2-drawer-sidebar--phone" : ""}`}
          >
            <SchoolDetailDrawer
              school={drawerSchool}
              onClose={handleCloseDrawer}
              selectedIds={selectedIds}
              onShowCatchment={toggleSchool}
              onOpenRanking={() => openPage("ranking")}
              journeyAvailable={hasSchoolJourney(drawerSchool)}
              onOpenJourney={openSchoolJourney}
              scrollToTopOnSchoolChange={isPhoneLayout}
            />
          </div>
        ) : null}

        {filterDrawerOpen ? (
          <div
            className={`v2-drawer-sidebar${isPhoneLayout ? " v2-drawer-sidebar--phone" : ""}`}
          >
            <FilterDrawer
              onClose={handleCloseFilters}
              filters={filters}
              updateFilter={updateFilter}
              resetFilters={resetFilters}
              hasActiveFilters={hasActiveFilters}
              schools={schools}
              filteredCount={Array.isArray(filteredSchools) ? filteredSchools.length : 0}
              isMobile={isPhoneLayout}
              filterMetaError={filterMetaError}
            />
          </div>
        ) : null}

        <div className="v2-map-wrap" style={MAP_WRAP_STYLE}>
          <MapCanvas center={homePosition} zoom={12}>
            {isPhoneLayout ? <PhoneMapViewportClamp /> : null}
            <PanToHomeV2 position={homePosition} />

            <HomeMarkerV2Layer position={homePosition} postcode={postcode} />
            <SetHomeOnMapClickV2
              onSetHome={setHomeFromMap}
              disabled={legendOpen}
            />

            <SchoolMarkersV2Layer
              schools={filteredSchools}
              highlightSchoolId={mapFocusSchool?.id ?? null}
              onSchoolMarkerClick={handleSchoolMarkerClick}
              ofstedBySchoolId={filterMeta.ofstedBySchoolId}
            />

            <SelectedCatchmentsLayers
              selectedIds={selectedIds}
              catchmentsBySchoolId={catchmentsBySchoolId}
            />
            <FitCatchmentBounds
              selectedIds={selectedIds}
              catchmentsBySchoolId={catchmentsBySchoolId}
            />

            <TransportRouteLayer route={transport.activeRoute} />
            <FitRouteBounds route={transport.activeRoute} />
            <FitHomeSchoolBounds
              homePosition={homePosition}
              school={transport.transportSchool}
            />

            <PanToSchoolV2
              schools={filteredSchools}
              selectedIds={selectedIds}
              catchmentsBySchoolId={catchmentsBySchoolId}
              pauseForTransport={!!transport.transportSchool}
            />

            <LegendControl
              open={legendOpen}
              onToggle={handleToggleLegend}
              onClose={handleCloseLegend}
            />

            <MapZoomIndicator />
          </MapCanvas>
        </div>

        {isPhoneLayout ? (
          <MobileSchoolCardsDeck
            homeScrollKey={`${homeLat ?? ""},${homeLon ?? ""}`}
            topItems={mobileTopItems}
            middleItems={mobileMiddleItems}
            nearbyItems={mobileNearbyItems}
            bottomItems={mobileBottomItems}
            selectedIds={selectedIds}
            onToggleSchool={toggleSchool}
            homeLocation={transport.homeLocation}
            transportSchool={transport.transportSchool}
            onOpenTransport={transport.toggleTransportForSchool}
            onCloseTransport={transport.closeTransport}
            onSelectRoute={transport.onSelectRoute}
            onHoverRoute={transport.onHoverRoute}
            onLeaveRoute={transport.onLeaveRoute}
            onLeaveOptionsList={transport.onLeaveOptionsList}
            onClearRoute={transport.onClearRoute}
            onOpenSchoolDetails={handleOpenSchoolDetails}
            onSchoolRowClick={handleSchoolRowClick}
            drawerSchoolId={drawerSchool?.id}
            focusedSchoolId={mapFocusSchool?.id ?? null}
            catchmentCheckBySchoolId={catchmentCheckBySchoolId}
            catchmentLoading={catchmentLoading}
            catchmentCheckError={showServerBanner ? null : catchmentCheckError}
            schoolsError={listSchoolsError}
            onClearAllCatchments={clearAllCatchments}
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
            favouritesFilterOn={favouritesFilterOn}
            favouriteCount={favouriteCount}
          />
        ) : null}
      </div>
      )}
    </div>
  );
}